import { SelectRadio } from '@/components/select-radio/SelectRadio';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import { FormatJSONCodeBlock } from '@/ui-kit/components/CodeBlock/FormatJSONCodeBlock';
import type { IHttpApiResponse } from '@tarslib/types/lib/httpApiResponse';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import clsx from 'clsx';
import React from 'react';
import { MarkdownViewer } from '../markdown-viewer/MarkdownViewer';
import styles from './ResponseExample.module.css';

export type ResponseExampleProps = {
  value: Pick<IHttpApiResponse, 'code' | 'contentType' | 'responseExamples' | 'id' | 'name'>[];
  mode?: ParameterDisplayMode;
  title?: React.ReactNode;
  className?: string;
};

export function ResponseExample(props: ResponseExampleProps) {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const {
    value,
    title = clientI18n('apiRunnerSplitPane.response'),
    mode = ParameterDisplayMode.CLASSIC,
    className,
  } = props;
  const activeKey = useOperationConfigModelStore((x) => x.selectedResponseKey);
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);
  const responseExamplesGroup = React.useMemo(() => {
    const responseCodeSet = new Set(value.map((x) => x.code));

    const languageMap: Record<(typeof value)[number]['contentType'], 'json' | 'plaintext' | 'xml'> =
      {
        json: 'json',
        raw: 'plaintext',
        xml: 'xml',
        html: 'xml',
        msgpack: 'json',
        eventStream: 'plaintext',
        binary: 'plaintext',
        noContent: 'plaintext',
        text: 'plaintext',
      };

    return value
      .map((x, i) => {
        const labelPrefix = (() => {
          if (mode === ParameterDisplayMode.CLASSIC) {
            return '';
          }
          if (value.length < 2) {
            return '';
          }

          if (responseCodeSet.size > 1) {
            return `${x.code} - `;
          } else {
            return `${x.code} - ${x.name} - `;
          }
        })();

        return {
          label: `${x.code} - ${x.name}`,
          options:
            x.responseExamples?.map((y) => {
              return {
                key: `${x.id}-${y.id}`,
                label: (
                  <span>
                    <span className={styles['label-prefix']}>{labelPrefix}</span>
                    {y.name}
                  </span>
                ),
                language: languageMap[x.contentType],
                code: y.data,
                value: `${x.id}-${y.id}`,
                parentKey: String(x.id ?? i),
                description: y.description,
              };
            }) ?? [],
        };
      })
      .filter((x) => !!x.options.length);
  }, [value, mode]);
  const responseExamples = React.useMemo(() => {
    return responseExamplesGroup.map((x) => x.options).flat();
  }, [responseExamplesGroup]);
  const responseExamplesRef = React.useRef(responseExamples);
  responseExamplesRef.current = responseExamples;

  const [responseExampleSelectedValue, setResponseExampleSelectedValue] = React.useState(
    () => responseExamples[0],
  );
  const responseExampleSelectedValueRef = React.useRef(responseExampleSelectedValue);
  responseExampleSelectedValueRef.current = responseExampleSelectedValue;

  React.useEffect(() => {
    setResponseExampleSelectedValue(responseExamples[0]);
  }, [responseExamples]);

  React.useEffect(() => {
    if (activeKey) {
      const currentKey = responseExampleSelectedValueRef.current?.parentKey;
      if (currentKey !== activeKey) {
        const targetValue = responseExamplesRef.current.find((x) => x.parentKey === activeKey);
        if (targetValue) {
          setResponseExampleSelectedValue(targetValue);
        } else {
          setResponseExampleSelectedValue(responseExamplesRef.current[0]);
        }
      }
    } else {
      setResponseExampleSelectedValue(responseExamplesRef.current[0]);
    }
  }, [activeKey]);

  const isModern = mode === ParameterDisplayMode.MODERN;

  const titleElement = (
    <div
      className={clsx('flex-shrink-0 text-base font-500', {
        ['ml-1 mr-4 py-[1px] text-color-dark']: isModern,
        ['ml-1 mr-2 text-color']: !isModern,
      })}
    >
      {title}
    </div>
  );

  if (!responseExampleSelectedValue) {
    return null;
  }
  return (
    <div
      className={clsx(
        {
          ['rounded-xl bg-code-viewer']: isModern,
        },
        className,
      )}
    >
      <div
        className={clsx('flex px-3 py-2', {
          ['items-center border-b border-b-[#222A39]']: isModern,
          ['items-center pt-4']: !isModern,
        })}
      >
        <SelectRadio
          value={responseExampleSelectedValue.value}
          options={isModern ? responseExamplesGroup : responseExamples}
          onChange={(v) => {
            setResponseExampleSelectedValue(responseExamples.find((x) => x.value === v));
          }}
          className={clsx({
            ['flex w-0 flex-1 justify-between']: isModern,
          })}
          minShowItemCount={1}
          type={isModern ? 'select' : 'radio'}
          prefixNode={titleElement}
          customCalcLength={() => responseExamples.length}
          popupClassName={styles['response-example-select-popup']}
        />
      </div>
      {responseExampleSelectedValue.description && (
        <div
          className={
            apiDocLayout === ApiDocLayout.OneColumn
              ? styles.description
              : styles.description_three_column
          }
        >
          <MarkdownViewer
            value={responseExampleSelectedValue.description}
            enableHeaderPlugin={false}
            inDocView={false}
          />
        </div>
      )}
      <FormatJSONCodeBlock
        code={responseExampleSelectedValue.code}
        language={responseExampleSelectedValue.language}
        highlightTheme={isModern ? 'github-dark' : undefined}
        theme={!isModern ? 'light' : undefined}
        onlyCode={false}
        className={clsx({
          [`p-0 ${styles['code-viewer-classic']}`]: mode === ParameterDisplayMode.CLASSIC,
          [`max-h-[500px] flex-1 @[850px]/http-api-content:h-0 @[850px]/http-api-content:max-h-[unset] ${styles['code-viewer-modern']}`]:
            mode === ParameterDisplayMode.MODERN,
        })}
      />
    </div>
  );
}
